import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { brand } from "@/config/brand";
import { scrollToId, scrollToPricing } from "@/lib/scroll";

const LINKS = [
  { label: "How It Works", id: "how" },
  { label: "AI Agents", id: "agents" },
  { label: "Pricing", id: "pricing" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-black border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-[1280px] items-center justify-between px-5 sm:px-8">
        {/* Logo only */}
        <button
          data-testid="navbar-logo"
          onClick={() => scrollToId("top", 0)}
          className="flex shrink-0 items-center"
          aria-label={brand.brand_name}
        >
          <img
            src={brand.logo_url}
            alt={brand.brand_name}
            className="h-14 w-auto object-contain"
            style={{ maxWidth: 260 }}
          />
        </button>

        {/* Center links */}
        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative text-sm font-medium tracking-wide text-white/80 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-white transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        {/* Right actions */}
        <div className="hidden items-center gap-3 lg:flex">
          <button
            data-testid="nav-login-btn"
            onClick={() => { window.location.href = "https://app.smartweb.host/try/#/login"; }}
            className="rounded-[10px] border border-white/30 bg-transparent px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-white hover:bg-white/5"
          >
            Login
          </button>
          <button
            data-testid="nav-cta-btn"
            onClick={scrollToPricing}
            className="rounded-[10px] bg-white px-5 py-2.5 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5 hover:bg-white/90"
          >
            Get Started
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-white/20 text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-white/10 bg-black px-5 pb-6 pt-2 lg:hidden"
        >
          <div className="flex flex-col">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="border-b border-white/5 py-4 text-left text-base font-medium text-white/85"
              >
                {l.label}
              </button>
            ))}
          </div>
          <div className="mt-5 flex flex-col gap-3">
            <button
              data-testid="mobile-nav-login-btn"
              onClick={() => { window.location.href = "https://app.smartweb.host/try/#/login"; }}
              className="rounded-[10px] border border-white/30 py-3 text-sm font-medium text-white"
            >
              Login
            </button>
            <button
              data-testid="mobile-nav-cta-btn"
              onClick={() => go("pricing")}
              className="rounded-[10px] bg-white py-3 text-sm font-semibold text-black"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
